import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KEYCODES } from '@Utils/KeyboardUtil';
import trim from 'lodash/trim';
import PropTypes from 'prop-types';
import Tooltip from '@Components/Tooltip';
import { ValidatedTextInput } from '@els/els-react--text-input';
import { ButtonWithIcon } from '@els/els-react--button';
import HoverTooltip from '@Components/HoverTooltip';
import { onProjectNameEdit } from '../RetroSynthesisProjects/analytics';

import './RsEditableItem.scss';

function RsEditableItem({
  value: initialValue,
  onValueEdited = () => {},
  maxChars = 50,
  showTruncationTooltip = true,
  validateValueInput = () => true,
  isEditMode = false,
  setEditMode,
  trackingContent,
}) {
  const [alternateValue, setAlternateValue] = useState(initialValue);
  const [displayTruncationTooltip, setDisplayTruncationTooltip] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const labelRef = useRef(null);

  const buttonId = useMemo(() => `toggle-edit-mode-${initialValue.replace(/\s/g, '-')}`, [initialValue]);

  const closeEditMode = useCallback(
    (action) => {
      onProjectNameEdit({ action, trackingContent });
      setEditMode(false);
      setTimeout(() => {
        labelRef.current?.focus?.();
      }, 0);
    },
    [setEditMode, trackingContent]
  );

  const doSave = useCallback(() => {
    if (trim(alternateValue) && alternateValue !== initialValue) {
      onValueEdited(alternateValue);
      closeEditMode('complete');
    } else {
      closeEditMode('abandoned');
    }
  }, [onValueEdited, alternateValue, initialValue, closeEditMode]);

  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.keyCode === KEYCODES.ENTER) {
        doSave();
      }
      if (e.keyCode === KEYCODES.ESC) {
        closeEditMode('abandoned');
      }
    },
    [doSave, closeEditMode]
  );

  const handleButtonClick = useCallback(() => {
    setEditMode(!isEditMode);
  }, [isEditMode, setEditMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isEditMode) return;

      if (wrapperRef.current && !wrapperRef.current?.contains(event.target)) {
        closeEditMode('abandoned');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditMode, closeEditMode]);

  // Reset value when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      setAlternateValue(initialValue);
    }
  }, [isEditMode, initialValue]);

  useEffect(() => {
    const el = labelRef.current;
    setDisplayTruncationTooltip(el ? el.scrollWidth > el.clientWidth : false);
  }, [initialValue]);

  useEffect(() => {
    if (displayTruncationTooltip) {
      Tooltip.rebuild();
    }
  }, [displayTruncationTooltip]);

  useEffect(() => {
    if (isEditMode) {
      onProjectNameEdit({ action: 'start', trackingContent });
    }
  }, [isEditMode, trackingContent]);

  useEffect(() => {
    let focusTimer;
    if (isEditMode) {
      focusTimer = setTimeout(() => inputRef.current?.focus?.(), 0);
    }
    return () => clearTimeout(focusTimer);
  }, [isEditMode]);

  const resolveProjectNameError = useCallback(() => {
    const trimmedValue = trim(alternateValue || '');

    if (!trimmedValue) return 'Project name is required.';
    if (trimmedValue.length < 5) return 'Project name is too short, minimum length is 5 characters.';
    if (maxChars && trimmedValue.length > maxChars) return `Project name is too long, maximum length is ${maxChars} characters.`;
    if (!validateValueInput(alternateValue)) return 'Please enter a valid project name.';

    return null;
  }, [alternateValue, maxChars, validateValueInput]);

  const ariaText = isEditMode ? 'Save project name' : 'Edit project name';

  const editButton = (
    <ButtonWithIcon
      data-e2e="editprojectname-button"
      ref={labelRef}
      id={buttonId}
      className="rs-editable-item-non-editable-button"
      type="link"
      onClick={handleButtonClick}
      sprite="Edit"
      size="small"
      iconRight
      linkIconSize="xs"
      aria-label={`Edit ${initialValue || ''},  project name`}>
      {initialValue || ''}
    </ButtonWithIcon>
  );

  const nonEditModeView =
    showTruncationTooltip && displayTruncationTooltip ? (
      <HoverTooltip tooltip={initialValue || ''} theme="simple">
        {editButton}
      </HoverTooltip>
    ) : (
      editButton
    );

  return (
    <div ref={wrapperRef} className="rs-editable-item">
      {isEditMode ? (
        <div className="rs-editable-item__edit-mode">
          <div className="rs-editable-item__edit-row">
            <div className="rs-editable-item__input-wrapper">
              <ValidatedTextInput
                data-e2e="edit-project-name"
                value={alternateValue}
                label="Edit Project Name"
                shouldHideLabel
                inputRef={inputRef}
                required
                onlyValidateWhenTouched
                validator={resolveProjectNameError}
                min={5}
                max={maxChars}
                onKeyDown={handleInputKeyDown}
                onChange={(e) => {
                  const nextValue = e?.currentTarget?.value ?? e?.target?.value ?? '';
                  setAlternateValue(nextValue);
                }}
              />
            </div>
            <ButtonWithIcon
              data-e2e="toggle-edit-mode-button"
              type="link"
              onClick={doSave}
              className="rs-editable-item__action-button"
              sprite="Checkmark"
              size="small"
              iconRight
              iconOnly
              linkIconSize="xs"
              aria-label={ariaText}>
              {ariaText}
            </ButtonWithIcon>
          </div>
        </div>
      ) : (
        nonEditModeView
      )}
    </div>
  );
}

RsEditableItem.propTypes = {
  value: PropTypes.string,
  onValueEdited: PropTypes.func,
  maxChars: PropTypes.number,
  showTruncationTooltip: PropTypes.bool,
  validateValueInput: PropTypes.func,
  isEditMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  trackingContent: PropTypes.array,
};

export default RsEditableItem;
